import React, { useState, useEffect, useCallback, useMemo } from 'react';
import {
  Modal,
  Box,
  Typography,
  TextField,
  Button,
  MenuItem,
  IconButton,
  FormControlLabel,
  Checkbox,
  Divider,
} from '@mui/material';
import CloseIcon from '@mui/icons-material/Close';
import api from '../../../config/axiosInstance';
import ConfirmExitDialog from './dialog/ConfirmExitDialog';
import CalendarSyncProgressOverlay from '../dialogs/CalendarSyncProgressOverlay';
import { DatePicker } from '@mui/x-date-pickers/DatePicker';
import WeeklyScheduleEditor from '../WeeklyScheduleEditor';
import AreaPositionSelector from '../AreaPositionSelector';
import RotationWorkshiftsPicker from '../RotationWorkshiftsPicker';
import {
  assignmentModeOptions,
  restPolicyOptions,
  suggestRestPolicyFromWorkarea,
} from '../planningPolicyOptions';
import {
  createEmptyWeeklySchedule,
  isWeeklyScheduleComplete,
  isWeeklyScheduleEmpty,
  normalizeWeeklySchedule,
  serializeWeeklySchedule,
  CYCLE_WEEK_ALL,
} from '../weeklyScheduleUtils';

const style = {
  position: 'absolute',
  top: '50%',
  left: '50%',
  transform: 'translate(-50%, -50%)',
  width: 'min(840px, 92vw)',
  maxHeight: '90vh',
  overflowY: 'auto',
  bgcolor: 'background.paper',
  border: '1px solid #D6E0EA',
  borderRadius: 2,
  boxShadow: 24,
  p: 4,
};

const sectionSx = {
  border: '1px solid #E3EAF2',
  borderRadius: 2,
  px: 2,
  py: 2.5,
  backgroundColor: '#FAFCFF',
};

const toggleCardSx = {
  border: '1px solid #DCE6F0',
  borderRadius: 2,
  px: 1.5,
  py: 1.25,
  minHeight: 88,
  backgroundColor: '#FFFFFF',
};

const AddPersonalModal = ({ open, handleClose, handleAddPersonal }) => {
  const initialEmployeeData = useMemo(
    () => ({
      ci: '',
      name: '',
      surname: '',
      // quitamos position/workarea separados
      override_manager: false,
      station: '',
      is_manager: false,
      is_night: false,
      override_night: false,
      employment_regime: 'monthly',
      rest_policy: 'standard_6',
      assignment_mode: 'regular',
      first_day: '',
    }),
    []
  );

  const [employeeData, setEmployeeData] = useState(initialEmployeeData);
  const [additionalData, setAdditionalData] = useState({
    positions: [],
    stations: [],
    workareas: [], // catálogo bruto del backend
    workshiftsByStation: {},
  });

  // NUEVO: selección combinada. primaryWAs: array de opciones { id, label,
  // positionId, positionName, roleName }, todas de la MISMA área -- la
  // primera es el id_workarea principal, el resto (si hace más de una
  // posición dentro de la misma área) se manda como auxiliar igual que hoy.
  // auxWAs queda solo para auxiliares de OTRA área (flexibilidad real).
  const [primaryWAs, setPrimaryWAs] = useState([]);
  const [auxWAs, setAuxWAs] = useState([]);         // array de objetos opción

  const [formLabels, setFormLabels] = useState({});
  const [errors, setErrors] = useState({});
  const [isFormDirty, setIsFormDirty] = useState(false);
  const [confirmOpen, setConfirmOpen] = useState(false);
  const [saveConfirmOpen, setSaveConfirmOpen] = useState(false);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [weeklyScheduleSupported, setWeeklyScheduleSupported] = useState(false);
  const [weeklySchedule, setWeeklySchedule] = useState(createEmptyWeeklySchedule);
  const [stationWorkshifts, setStationWorkshifts] = useState([]);
  const [rotationWorkshiftSupported, setRotationWorkshiftSupported] = useState(false);
  const [rotationWorkshifts, setRotationWorkshifts] = useState([]); // array de { id, name }

  const resetForm = useCallback(() => {
    setEmployeeData(initialEmployeeData);
    setPrimaryWAs([]);
    setAuxWAs([]);
    setErrors({});
    setWeeklySchedule(createEmptyWeeklySchedule());
    setStationWorkshifts([]);
    setRotationWorkshifts([]);
  }, [initialEmployeeData]);

  useEffect(() => {
    if (open) fetchAdditionalData();
    if (!open) resetForm();
  }, [open, resetForm]);

  const fetchAdditionalData = () => {
    setLoading(true);
    api
      .get('/personal/add_info')
      .then((response) => {
        const formData = response.data[0].form[0];
        setFormLabels({
          ci: formData.ci,
          name: formData.names,
          surname: formData.surnames,
          is_manager: '¿Es encargado?',
          override_manager: '¿Puede cubrir a Encargado?',
          is_night: formData.is_night || '¿Puede hacer nocturnidad?',
          override_night: formData.override_night || '¿Puede cubrir turno nocturno?',
          employment_regime: formData.employment_regime || 'Régimen laboral',
          rest_policy: formData.rest_policy || 'Política de descansos',
          assignment_mode: formData.assignment_mode || 'Modalidad de asignación',
          first_day: 'Fecha de inicio',
          primary_combo: 'Área – Rol (principal)',
          aux_workareas: 'Roles auxiliares',
        });
        setAdditionalData({
          positions: formData.positions || [],
          stations: formData.station || [],
          workareas: formData.area || [],
          workshiftsByStation: response.data?.[0]?.workshifts_by_station || {},
        });
        setWeeklyScheduleSupported(Boolean(response.data?.[0]?.weekly_schedule_supported));
        setRotationWorkshiftSupported(Boolean(response.data?.[0]?.rotation_workshift_supported));
      })
      .catch((error) => console.error('Error fetching data:', error))
      .finally(() => setLoading(false));
  };

  // Mapa de áreas por id para rotular
  const positionNameById = useMemo(() => {
    const map = new Map();
    (additionalData.positions || []).forEach((p) => map.set(p.id, p.name));
    return map;
  }, [additionalData.positions]);

  // Opciones combinadas “Área – Rol”
  const workareaOptions = useMemo(() => {
    const seen = new Set();
    return (additionalData.workareas || [])
      .filter((wa) => wa && wa.id != null)
      .map((wa) => {
        const positionId = wa.position;
        const positionName = positionNameById.get(positionId) || 'Área';
        const roleName = wa.name || 'Posición';
        return {
          id: wa.id,
          roleName,
          positionId,
          positionName,
          label: `${positionName} – ${roleName}`,
        };
      })
      // evitar duplicados por id por si el backend clona registros
      .filter((opt) => (seen.has(opt.id) ? false : (seen.add(opt.id), true)))
      // orden alfabético por área y rol
      .sort((a, b) => (a.positionName + a.roleName).localeCompare(b.positionName + b.roleName));
  }, [additionalData.workareas, positionNameById]);

  const availableAssignmentModes = useMemo(
    () => assignmentModeOptions.filter((option) => {
      if (option.value === 'fixed_schedule') return weeklyScheduleSupported;
      if (option.value === 'restricted_rotation') return rotationWorkshiftSupported;
      return true;
    }),
    [weeklyScheduleSupported, rotationWorkshiftSupported]
  );

  const selectedStationWorkshifts = useMemo(() => {
    const stationId = Number(employeeData.station || 0);
    if (!stationId) {
      return [];
    }

    return additionalData.workshiftsByStation?.[String(stationId)] || [];
  }, [additionalData.workshiftsByStation, employeeData.station]);

  useEffect(() => {
    setStationWorkshifts(selectedStationWorkshifts);
    // si cambia la estación, los turnos elegidos de la anterior ya no aplican
    setRotationWorkshifts((prev) =>
      prev.filter((ws) => selectedStationWorkshifts.some((opt) => Number(opt.id) === Number(ws.id)))
    );
  }, [selectedStationWorkshifts]);

  const validateForm = () => {
    const newErrors = {};
    const must = ['ci', 'name', 'surname', 'station', 'first_day'];
    must.forEach((k) => {
      const v = employeeData[k];
      if (k !== 'first_day' && typeof v === 'string' && !v.trim()) {
        newErrors[k] = 'Este campo es obligatorio';
      }
      if (k === 'first_day' && !v) {
        newErrors[k] = 'Este campo es obligatorio';
      }
    });
    if (!primaryWAs?.length) newErrors.primaryWA = 'Seleccioná el Área y la Posición principal';
    if (employeeData.assignment_mode === 'fixed_schedule') {
      if (!weeklyScheduleSupported) {
        newErrors.assignment_mode = 'Esta base todavía no tiene habilitados los horarios fijos semanales.';
      } else if (isWeeklyScheduleEmpty(weeklySchedule)) {
        newErrors.weekly_schedule = 'Marcá al menos un día con turno fijo o libre.';
      } else if (!isWeeklyScheduleComplete(weeklySchedule)) {
        newErrors.weekly_schedule = 'Completá el turno o "Libre" para cada día marcado.';
      } else if (!employeeData.station) {
        newErrors.station = 'Seleccioná una estación antes de definir el horario fijo';
      }
    }
    if (employeeData.assignment_mode === 'restricted_rotation') {
      if (!rotationWorkshiftSupported) {
        newErrors.assignment_mode = 'Esta base todavía no tiene habilitada la rotación restringida.';
      } else if (!employeeData.station) {
        newErrors.station = 'Seleccioná una estación antes de elegir los turnos permitidos';
      } else if (!rotationWorkshifts.length) {
        newErrors.rotation_workshifts = 'Elegí al menos un turno permitido para rotar.';
      }
    }
    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleBasicChange = (e) => {
    const { name, type, checked, value } = e.target;
    let isValid = true;
    let errorMessage = '';

    if (name === 'ci' && !/^\d{0,10}$/.test(value)) {
      isValid = false;
      errorMessage = 'La cédula debe ser solo números y máximo 10 dígitos.';
    }
    if ((name === 'name' || name === 'surname') && !/^[a-zA-ZáéíóúÁÉÍÓÚñÑ \s]*$/.test(value)) {
      isValid = false;
      errorMessage = 'Solo se permiten letras en este campo.';
    }

    setEmployeeData((prev) => {
      const next = { ...prev, [name]: type === 'checkbox' ? checked : value };
      if (name === 'is_manager') next.override_manager = next.is_manager;
      if (name === 'override_manager' && !next.override_manager) next.is_manager = false;
      // Al marcar "Es encargado" la sección de días fijos queda visible y editable,
      // así que la modalidad debe reflejar eso para no quedar incoherente.
      if (name === 'is_manager' && next.is_manager && prev.assignment_mode === 'regular') {
        next.assignment_mode = 'fixed_schedule';
      }
      return next;
    });

    if (name === 'is_manager' && checked) {
      // Precarga visual del default que también siembra el backend al crear
      // un encargado sin días fijos configurados todavía -- editable antes de guardar.
      setWeeklySchedule((prev) =>
        isWeeklyScheduleEmpty(prev)
          ? [{ weekday: 6, cycle_week: CYCLE_WEEK_ALL, workshift_id: '', is_free_day: true }]
          : prev
      );
    }

    setErrors((prev) => ({ ...prev, [name]: isValid ? '' : errorMessage }));
    setIsFormDirty(true);
  };

  const parseDateValue = (value) => {
    if (!value) return null;
    const parsed = value instanceof Date ? value : new Date(`${value}T00:00:00`);
    return Number.isNaN(parsed.getTime()) ? null : parsed;
  };

  const handleDateChange = (date) => {
    const parsedDate = parseDateValue(date);
    const formattedDate = parsedDate ? parsedDate.toISOString().split('T')[0] : '';
    setEmployeeData((prev) => ({ ...prev, first_day: formattedDate }));
    setErrors((prev) => ({
      ...prev,
      first_day: date && !parsedDate ? 'Ingresá una fecha válida' : '',
    }));
    setIsFormDirty(true);
  };

  const handlePolicyChange = (name, value) => {
    setEmployeeData((prev) => ({ ...prev, [name]: value }));
    setErrors((prev) => ({
      ...prev,
      [name]: '',
      ...(name === 'assignment_mode' ? { weekly_schedule: '' } : {}),
    }));
    setIsFormDirty(true);
  };

  const handleSubmit = () => {
    if (!validateForm()) return;

    const resolvedStation = (additionalData.stations || []).find(
      (s) => Number(s?.id) === Number(employeeData.station)
    );

    const primaryWA = primaryWAs?.[0] || null;
    if (!resolvedStation || !primaryWA) {
      setErrors((prev) => ({
        ...prev,
        station: resolvedStation ? '' : 'Seleccioná una estación válida',
        primaryWA: primaryWA ? '' : 'Seleccioná el Área y la Posición principal',
      }));
      return;
    }

    setSaveConfirmOpen(true);
  };

  const handleConfirmSave = async () => {
    setSaveConfirmOpen(false);
    const resolvedStation = (additionalData.stations || []).find(
      (s) => Number(s?.id) === Number(employeeData.station)
    );
    const primaryWA = primaryWAs?.[0] || null;
    if (!resolvedStation || !primaryWA) {
      return;
    }

    // Aux: chips extra de posición dentro de la misma área (además de la
    // principal) + auxiliares de otra área, sin el principal.
    const extraPrimaryIds = (primaryWAs || []).slice(1).map((o) => o?.id);
    const auxIds = [...extraPrimaryIds, ...(auxWAs || []).map((o) => o?.id)]
      .filter((id) => id != null && id !== primaryWA.id);

    const postData = {
      ci: employeeData.ci,
      name: employeeData.name,
      surname: employeeData.surname,
      override_manager: employeeData.override_manager ? 1 : 0,
      is_manager: employeeData.is_manager ? 1 : 0,
      is_night: employeeData.is_night ? 1 : 0,
      override_night: employeeData.override_night ? 1 : 0,
      station: resolvedStation.id,
      // lo que espera el backend:
      position: primaryWA.positionId,  // área del rol principal
      workarea: primaryWA.id,          // id del rol principal
      first_day: employeeData.first_day,
      employment_regime: 'monthly',
      rest_policy: employeeData.rest_policy,
      assignment_mode: employeeData.assignment_mode,
      aux_workareas: auxIds.join(','), // CSV; si preferís JSON: JSON.stringify(auxIds)
      weekly_schedule:
        employeeData.assignment_mode === 'fixed_schedule'
          ? JSON.stringify(serializeWeeklySchedule(weeklySchedule))
          : '',
      rotation_workshift_ids:
        employeeData.assignment_mode === 'restricted_rotation'
          ? JSON.stringify(rotationWorkshifts.map((ws) => ws.id))
          : '',
    };

    try {
      setSaving(true);
      await handleAddPersonal(postData);
      setIsFormDirty(false);
    } catch (error) {
      // El snackbar lo maneja el contenedor.
    } finally {
      setSaving(false);
    }
  };

  const renderTextField = (name, label, select = false, options = []) => (
    <TextField
      fullWidth
      select={select}
      disabled={saving}
      label={label}
      name={name}
      value={employeeData[name]}
      onChange={handleBasicChange}
      onInput={(e) => {
        if (name === 'ci') e.target.value = e.target.value.replace(/[^0-9]/g, '').slice(0, 10);
        if (name === 'name' || name === 'surname')
          e.target.value = e.target.value.replace(/[^a-zA-ZáéíóúÁÉÍÓÚñÑ ]/g, '');
      }}
      inputProps={{
        inputMode: name === 'ci' ? 'numeric' : 'text',
        pattern: name === 'ci' ? '[0-9]*' : (name === 'name' || name === 'surname') ? '[A-Za-záéíóúÁÉÍÓÚñÑ ]*' : undefined,
      }}
      error={Boolean(errors[name])}
      helperText={errors[name]}
    >
      {select &&
        options.map((option) => (
          <MenuItem key={option.id} value={option.name}>
            {option.name}
          </MenuItem>
        ))}
    </TextField>
  );

  const handleCloseModal = () => {
    if (saving) return;
    if (isFormDirty) setConfirmOpen(true);
    else handleClose();
  };

  const handleConfirmClose = () => {
    setConfirmOpen(false);
    handleClose();
    setIsFormDirty(false);
  };

  const renderToggleCard = (name, label, helperText, disabled = false) => (
    <Box sx={toggleCardSx}>
      <FormControlLabel
        control={
          <Checkbox
            name={name}
            checked={Boolean(employeeData[name])}
            onChange={handleBasicChange}
            disabled={disabled}
          />
        }
        label={
          <Box>
            <Typography variant="body2" sx={{ fontWeight: 600, color: '#16324F' }}>
              {label}
            </Typography>
            <Typography variant="caption" sx={{ display: 'block', color: 'text.secondary', lineHeight: 1.35 }}>
              {helperText}
            </Typography>
          </Box>
        }
        sx={{ alignItems: 'flex-start', m: 0 }}
      />
    </Box>
  );

  return (
    <>
      <Modal
        open={open}
        onClose={() => {
          if (saving) return;
          handleCloseModal();
        }}
        aria-labelledby="modal-title"
        aria-describedby="modal-description"
      >
        <Box sx={style} aria-busy={saving}>
          <IconButton
            sx={{ position: 'absolute', top: 8, right: 8 }}
            onClick={handleCloseModal}
            disabled={saving}
          >
            <CloseIcon />
          </IconButton>

          {loading ? (
            <Typography align="center">Cargando...</Typography>
          ) : (
            <>
              <Typography id="modal-title" variant="h6" component="h2" align="center">
                Ingresar nuevo empleado
              </Typography>

              <Box sx={{ mt: 3, display: 'flex', flexDirection: 'column', gap: 2.5 }}>
                <Box sx={sectionSx}>
                  <Typography variant="subtitle2" sx={{ mb: 0.5, fontWeight: 700, color: '#16324F' }}>
                    Datos personales
                  </Typography>
                  <Typography variant="body2" sx={{ mb: 2, color: 'text.secondary' }}>
                    Información básica para identificar al empleado.
                  </Typography>

                  <Box
                    sx={{
                      display: 'grid',
                      gridTemplateColumns: { xs: '1fr', md: '1fr 1fr' },
                      gap: 2,
                    }}
                  >
                    {renderTextField('ci', formLabels.ci)}
                    <DatePicker
                      disabled={saving}
                      label={formLabels.first_day}
                      value={parseDateValue(employeeData.first_day)}
                      onChange={handleDateChange}
                      renderInput={(params) => (
                        <TextField
                          {...params}
                          fullWidth
                          error={Boolean(errors.first_day)}
                          helperText={errors.first_day}
                        />
                      )}
                    />
                    <Box sx={{ gridColumn: { xs: 'auto', md: '1 / -1' } }}>
                      {renderTextField('name', formLabels.name)}
                    </Box>
                    <Box sx={{ gridColumn: { xs: 'auto', md: '1 / -1' } }}>
                      {renderTextField('surname', formLabels.surname)}
                    </Box>
                  </Box>
                </Box>

                <Box sx={sectionSx}>
                  <Typography variant="subtitle2" sx={{ mb: 0.5, fontWeight: 700, color: '#16324F' }}>
                    Asignación
                  </Typography>
                  <Typography variant="body2" sx={{ mb: 2, color: 'text.secondary' }}>
                    Define el rol principal, apoyos posibles y estación de trabajo.
                  </Typography>

                  <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
                    <AreaPositionSelector
                      multiple
                      options={workareaOptions}
                      disabled={saving}
                      value={primaryWAs}
                      onChange={(value) => {
                        const nextPrimaryWAs = value || [];
                        const nextPrimaryIds = new Set(nextPrimaryWAs.map((o) => o.id));
                        const suggestedRestPolicy = suggestRestPolicyFromWorkarea(nextPrimaryWAs[0] || null);
                        setPrimaryWAs(nextPrimaryWAs);
                        setAuxWAs((prev) => prev.filter((o) => !nextPrimaryIds.has(o.id)));
                        setEmployeeData((prev) => ({
                          ...prev,
                          rest_policy: suggestedRestPolicy,
                        }));
                        setIsFormDirty(true);
                        setErrors((prev) => ({ ...prev, primaryWA: '' }));
                      }}
                      error={Boolean(errors.primaryWA)}
                      helperText={errors.primaryWA || 'Rol base desde el que trabajará habitualmente.'}
                    />

                    <AreaPositionSelector
                      multiple
                      options={workareaOptions}
                      disabled={saving}
                      value={auxWAs}
                      excludeAreaIds={primaryWAs?.[0]?.positionId != null ? [primaryWAs[0].positionId] : []}
                      onChange={(value) => {
                        setAuxWAs(value || []);
                        setIsFormDirty(true);
                      }}
                      areaLabel="Área auxiliar"
                      positionLabel="Posición auxiliar"
                    />

                    <TextField
                      select
                      fullWidth
                      disabled={saving}
                      label={formLabels.assignment_mode || 'Modalidad de asignación'}
                      value={employeeData.assignment_mode}
                      onChange={(e) => handlePolicyChange('assignment_mode', e.target.value)}
                      error={Boolean(errors.assignment_mode)}
                      helperText={errors.assignment_mode || 'Si es ocasional, el calendario lo dejará en S/A hasta que se le asigne turno manualmente.'}
                    >
                      {availableAssignmentModes.map((option) => (
                        <MenuItem key={option.value} value={option.value}>
                          {option.label}
                        </MenuItem>
                      ))}
                    </TextField>

                    <TextField
                      select
                      fullWidth
                      disabled={saving}
                      label="Estación"
                      name="station"
                      value={employeeData.station}
                      onChange={handleBasicChange}
                      error={Boolean(errors.station)}
                      helperText={errors.station}
                    >
                      {(additionalData.stations || []).map((option) => (
                        <MenuItem key={option.id} value={String(option.id)}>
                          {option.name}
                        </MenuItem>
                      ))}
                    </TextField>

                    {employeeData.assignment_mode === 'fixed_schedule' && (
                      <WeeklyScheduleEditor
                        value={normalizeWeeklySchedule(weeklySchedule)}
                        onChange={(nextRows) => {
                          setWeeklySchedule(normalizeWeeklySchedule(nextRows));
                          setErrors((prev) => ({ ...prev, weekly_schedule: '' }));
                          setIsFormDirty(true);
                        }}
                        workshiftOptions={stationWorkshifts}
                        disabled={saving}
                        error={errors.weekly_schedule}
                        helperText={
                          stationWorkshifts.length === 0
                            ? 'Los turnos disponibles se cargan según la estación seleccionada.'
                            : 'Usá Libre para descansos fijos como domingos o feriados semanales.'
                        }
                      />
                    )}

                    {employeeData.assignment_mode === 'restricted_rotation' && (
                      <RotationWorkshiftsPicker
                        workshifts={stationWorkshifts}
                        value={rotationWorkshifts}
                        onChange={(newValue) => {
                          setRotationWorkshifts(newValue);
                          setErrors((prev) => ({ ...prev, rotation_workshifts: '' }));
                          setIsFormDirty(true);
                        }}
                        disabled={saving}
                        error={Boolean(errors.rotation_workshifts)}
                        helperText={errors.rotation_workshifts}
                      />
                    )}

                    <TextField
                      select
                      fullWidth
                      disabled={saving}
                      label={formLabels.rest_policy || 'Política de descansos'}
                      value={employeeData.rest_policy}
                      onChange={(e) => handlePolicyChange('rest_policy', e.target.value)}
                      helperText="Se sugiere según el rol principal, pero podés ajustarla."
                    >
                      {restPolicyOptions.map((option) => (
                        <MenuItem key={option.value} value={option.value}>
                          {option.label}
                        </MenuItem>
                      ))}
                    </TextField>
                  </Box>
                </Box>

                <Box sx={sectionSx}>
                  <Typography variant="subtitle2" sx={{ mb: 0.5, fontWeight: 700, color: '#16324F' }}>
                    Permisos operativos
                  </Typography>
                  <Typography variant="body2" sx={{ mb: 2, color: 'text.secondary' }}>
                    Marca las capacidades especiales que influyen en cobertura y planificación.
                  </Typography>

                  <Box
                    sx={{
                      display: 'grid',
                      gridTemplateColumns: { xs: '1fr', md: '1fr 1fr' },
                      gap: 1.5,
                    }}
                  >
                    {renderToggleCard(
                      'is_manager',
                      formLabels.is_manager,
                      'Queda habilitado como encargado principal del área.'
                    )}
                    {renderToggleCard(
                      'override_manager',
                      formLabels.override_manager,
                      'Puede cubrir funciones de encargado cuando haga falta.',
                      employeeData.is_manager
                    )}
                    {renderToggleCard(
                      'is_night',
                      formLabels.is_night,
                      'Entra en la rotación estructural de turnos nocturnos.'
                    )}
                    {renderToggleCard(
                      'override_night',
                      formLabels.override_night,
                      'Puede tomar un turno nocturno de forma manual para cobertura puntual.'
                    )}
                  </Box>
                </Box>

                <Divider flexItem />
              </Box>

              <Box sx={{ display: 'flex', justifyContent: 'flex-end', gap: 1.5, mt: 2.5 }}>
                <Button variant="outlined" color="inherit" onClick={handleCloseModal} disabled={saving}>
                  Cancelar
                </Button>
                <Button variant="contained" onClick={handleSubmit} disabled={saving}>
                  {saving ? 'Guardando...' : 'Guardar'}
                </Button>
              </Box>
            </>
          )}
        </Box>
      </Modal>

      <ConfirmExitDialog
        open={confirmOpen}
        onClose={() => setConfirmOpen(false)}
        onConfirm={handleConfirmClose}
      />
      <ConfirmExitDialog
        open={saveConfirmOpen}
        onClose={() => setSaveConfirmOpen(false)}
        onConfirm={handleConfirmSave}
        title="Confirmar alta"
        message="Se guardará el empleado y, si existen calendarios abiertos para la estación y roles seleccionados desde la fecha de inicio, también se actualizarán dejándolo con turno S/A para asignación manual posterior."
        confirmText="Guardar y actualizar"
      />
      <CalendarSyncProgressOverlay open={saving} variant="create" />
    </>
  );
};

export default AddPersonalModal;
